import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { GlassPanel } from "../components/GlassPanel";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar, PieChart, Pie, Cell, Legend,
} from "recharts";
import { useAuth } from "../context/AuthContext";
import { TrendingUp, DollarSign, Wrench, Activity, Download } from "lucide-react";
import InstallerAnalyticsPanel from "../components/InstallerAnalyticsPanel";
import ReturnNudgesPanel from "../components/ReturnNudgesPanel";
import { toast } from "sonner";

const STATUS_COLORS = {
  scheduled: "#3B82F6",
  in_progress: "#F59E0B",
  completed: "#10B981",
  cancelled: "#EF4444",
};

export default function AnalyticsPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    api.get("/analytics/summary").then(({ data }) => setData(data));
  }, []);

  if (!data) return <div className="text-slate-400">Loading analytics…</div>;

  const downloadRevenueCsv = async () => {
    try {
      const { data: blob } = await api.get("/analytics/revenue-csv", { responseType: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const today = new Date().toISOString().slice(0, 10);
      a.download = `wrapconsole-revenue-${today}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Revenue snapshot downloaded");
    } catch (err) {
      toast.error("Could not download CSV");
    }
  };

  const statusData = (data.status_distribution || []).map((s) => ({
    name: s.status.replace("_", " "),
    value: s.count,
    color: STATUS_COLORS[s.status] || "#94A3B8",
  }));

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-label">Insights</div>
          <h1 className="font-heading text-4xl font-light tracking-tighter text-white">Analytics</h1>
          <p className="text-slate-400 mt-2 text-sm">
            {isAdmin ? "Shop-wide performance and conversion." : "Your assigned jobs and personal output."}
          </p>
        </div>
        {isAdmin && (
          <button
            data-testid="analytics-revenue-csv"
            onClick={downloadRevenueCsv}
            className="flex items-center gap-2 bg-white text-black rounded-full px-4 py-2.5 text-sm font-semibold hover:bg-slate-100 transition"
          >
            <Download className="w-4 h-4" /> Revenue snapshot (CSV)
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi testId="analytics-total-jobs" label="Total Jobs" value={data.total_jobs} icon={Wrench} color="text-blue-300 bg-blue-500/10 border-blue-400/20" />
        <Kpi testId="analytics-completed" label="Completed" value={data.completed_jobs} icon={TrendingUp} color="text-emerald-300 bg-emerald-500/10 border-emerald-400/20" />
        <Kpi testId="analytics-in-progress" label="In Progress" value={data.in_progress_jobs} icon={Activity} color="text-amber-300 bg-amber-500/10 border-amber-400/20" />
        {isAdmin && (
          <Kpi testId="analytics-revenue" label="Revenue (Paid)" value={`$${Number(data.revenue || 0).toLocaleString()}`} icon={DollarSign} color="text-violet-300 bg-violet-500/10 border-violet-400/20" />
        )}
        {!isAdmin && (
          <Kpi testId="analytics-scheduled" label="Scheduled" value={data.scheduled_jobs} icon={Activity} color="text-violet-300 bg-violet-500/10 border-violet-400/20" />
        )}
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-6">
        {isAdmin && (
          <GlassPanel className="!p-6" delay={0.05}>
            <div className="text-label">Last 7 days</div>
            <h3 className="font-heading text-xl text-white mt-1 mb-4">Leads & Jobs trend</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.last_7_days || []}>
                  <defs>
                    <linearGradient id="cg1" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#3B82F6" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="cg2" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10B981" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="date" tick={{ fill: "#64748B", fontSize: 11 }} tickFormatter={(d) => d?.slice(5)} />
                  <YAxis tick={{ fill: "#64748B", fontSize: 11 }} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: "rgba(10,10,10,0.9)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12 }} />
                  <Area type="monotone" dataKey="leads" stroke="#3B82F6" fill="url(#cg1)" strokeWidth={2} />
                  <Area type="monotone" dataKey="jobs" stroke="#10B981" fill="url(#cg2)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </GlassPanel>
        )}

        <GlassPanel className="!p-6" delay={0.08}>
          <div className="text-label">Job status</div>
          <h3 className="font-heading text-xl text-white mt-1 mb-4">Distribution</h3>
          <div className="h-64">
            {statusData.length === 0 ? (
              <div className="h-full flex items-center justify-center text-slate-500 italic text-sm">No jobs to chart yet.</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={4}>
                    {statusData.map((s, i) => <Cell key={i} fill={s.color} stroke="rgba(0,0,0,0.3)" />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "rgba(10,10,10,0.9)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12, color: "#94A3B8" }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </GlassPanel>

        {isAdmin && (
          <GlassPanel className="!p-6 lg:col-span-2" delay={0.1}>
            <div className="text-label">Pipeline</div>
            <h3 className="font-heading text-xl text-white mt-1 mb-4">Volume snapshot</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={[
                  { label: "Leads", value: data.leads_count },
                  { label: "Quotes", value: data.quotes_count },
                  { label: "Invoices", value: data.invoices_count },
                  { label: "Contacts", value: data.contacts_count },
                  { label: "Jobs", value: data.total_jobs },
                ]}>
                  <CartesianGrid stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="label" tick={{ fill: "#94A3B8", fontSize: 12 }} />
                  <YAxis tick={{ fill: "#64748B", fontSize: 11 }} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: "rgba(10,10,10,0.9)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12 }} />
                  <Bar dataKey="value" fill="#3B82F6" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </GlassPanel>
        )}
      </div>

      {isAdmin && <InstallerAnalyticsPanel />}
      {isAdmin && <ReturnNudgesPanel />}
    </div>
  );
}

function Kpi({ label, value, icon: Icon, color, testId }) {
  return (
    <GlassPanel className="!p-5" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-label">{label}</div>
        <div className={`w-8 h-8 rounded-xl border flex items-center justify-center ${color}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <div className="font-heading text-3xl font-light tracking-tight text-white">{value}</div>
    </GlassPanel>
  );
}
